import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Quote, Star } from "lucide-react";

interface Testimonial {
  name: string;
  role: string;
  company: string;
  avatar: string;
  rating: number;
  text: string;
}

interface Props {
  testimonials: Testimonial[];
}

const TestimonialsCarousel = ({ testimonials }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Duplicate items for infinite scroll effect
  const items = [...testimonials, ...testimonials, ...testimonials];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let animationId: number;
    let scrollPos = 0;

    const step = () => {
      if (!isPaused && el) {
        scrollPos += 0.5;
        // Reset seamlessly when we've scrolled past one full set
        const singleSetWidth = el.scrollWidth / 3;
        if (scrollPos >= singleSetWidth) {
          scrollPos -= singleSetWidth;
        }
        el.scrollLeft = scrollPos;
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  return (
    <section id="testimonials" className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-gradient-brand">O Que Nossos Clientes Dizem</span>
          </h2>
          <p className="text-muted-foreground">Depoimentos reais de quem já transformou suas vendas</p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-6 overflow-hidden px-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {items.map((testimonial, index) => (
          <Card
            key={index}
            className="p-6 border-border bg-card hover:border-primary/30 transition-all flex-shrink-0 w-[350px]"
          >
            <Quote className="w-8 h-8 text-primary/30 mb-4" />
            <p className="text-muted-foreground text-sm mb-4 italic">"{testimonial.text}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold text-sm">
                {testimonial.avatar}
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground">{testimonial.name}</p>
                <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                <p className="text-xs text-primary">{testimonial.company}</p>
              </div>
            </div>
            <div className="flex gap-1 mt-3">
              {Array.from({ length: testimonial.rating }).map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-primary text-primary" />
              ))}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
